import { create } from "zustand";
import { useAuthStore } from "@/features/auth/auth-store";

const API_BASE: string = import.meta.env.VITE_API_BASE_URL || "/api";

interface CreditsState {
  balance: number | null;
  customerId: string | null;
  loading: boolean;
  deducting: boolean;
  error: string | null;

  fetchBalance: () => Promise<void>;
  deductCredit: (dialogId: string, dialogTitle: string) => Promise<{ success: boolean; balance?: number; error?: string }>;
  reset: () => void;
}

function getAuthHeader(): Record<string, string> {
  const user = useAuthStore.getState().user;
  if (!user?.idToken) return {};
  return { Authorization: user.idToken };
}

export const useCreditsStore = create<CreditsState>((set, get) => ({
  balance: null,
  customerId: null,
  loading: false,
  deducting: false,
  error: null,

  fetchBalance: async () => {
    const user = useAuthStore.getState().user;
    if (!user?.idToken) return;

    set({ loading: true, error: null });
    try {
      const resp = await fetch(`${API_BASE}/credits/balance`, {
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
      });

      if (!resp.ok) {
        set({ loading: false, error: "Failed to load balance" });
        return;
      }

      const data = await resp.json() as { customer_id?: string; balance?: number };
      set({
        balance: data.balance ?? null,
        customerId: data.customer_id ?? null,
        loading: false,
      });
    } catch {
      set({ loading: false, error: "Failed to load balance" });
    }
  },

  deductCredit: async (dialogId, dialogTitle) => {
    if (get().deducting) return { success: false, error: "Already processing" };

    set({ deducting: true, error: null });
    try {
      const resp = await fetch(`${API_BASE}/credits/deduct`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ dialog_id: dialogId, dialog_title: dialogTitle }),
      });

      const data = await resp.json() as Record<string, unknown>;

      if (resp.ok && data["success"]) {
        const remaining = data["remaining_balance"] as number;
        set({ balance: remaining, deducting: false });
        return { success: true, balance: remaining };
      }

      if (resp.status === 402) {
        const bal = (data["balance"] as number) ?? 0;
        set({ balance: bal, deducting: false, error: "Insufficient credits" });
        return { success: false, balance: bal, error: "Insufficient credits" };
      }

      const errMsg = (data["error"] as string) ?? "Failed to deduct credit";
      set({ deducting: false, error: errMsg });
      return { success: false, error: errMsg };
    } catch {
      set({ deducting: false, error: "Network error" });
      return { success: false, error: "Network error" };
    }
  },

  reset: () => set({ balance: null, customerId: null, loading: false, deducting: false, error: null }),
}));
